package top.wcpe.allincore.ui.runtime.ir.prop

import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.ir.DslRuntimeError
import top.wcpe.allincore.ui.runtime.ir.eval.ValueCoercion
import top.wcpe.allincore.ui.runtime.render.model.GuiBlendMode
import top.wcpe.allincore.ui.runtime.style.StyleVisual

/**
 * 效果装饰器属性贡献者：把 `effect.<kind>.<field>` 前缀的 prop key 路由到现有视觉子段实现，
 * 作为 DSL `effects { shadow { ... } }` 语法糖的运行时承接点。
 *
 * shadow 复用 VisualPropContributor 已注册的底层 binding；neon/ripple/texture/flow/energy/filter/
 * backdrop/blend 直接登记到 [StyleVisual] 对应字段，shader 集成留待真机阶段补齐（runtime 仅承接 prop
 * 路由，不直接驱动 GPU 管线）。IR / Codec 零改动——Parser 在 AST 阶段已把 DSL 块展平为 `effect.*` prop key。
 *
 * M6 错误一致性（E763）：[ValueCoercion] 的 `toXxx` 类型不匹配时抛 [IllegalStateException]，本 contributor
 * 注册的所有 `effect.*` binding 外加一层 [wrap]：捕获 ISE 后改抛 [DslRuntimeError] 并打印元素 id，
 * 既不污染 [ValueCoercion] 自身（普通 prop 仍走 ISE），又把 effects 路径上的类型错误归一到 DSL 运行时错误。
 */
internal object EffectPropContributor {

    private val LOGGER = UiLog("top.wcpe.allincore.ui.runtime.ir.prop.EffectPropContributor")

    // flow.mode 取值上界（0 linear / 1 radial / 2 diagonal）。
    private const val MAX_FLOW_MODE = 2

    // energy.pattern 取值上界（0 pulse / 1 wave / 2 arc / 3 plasma）。
    private const val MAX_ENERGY_PATTERN = 3

    /**
     * 注册 `effect.*` 别名/直登记，必须在 [VisualPropContributor.contribute] 之后调用。
     * 未找到底层 binding 时打印 warning 并跳过，避免启动期抛异常。
     */
    @Suppress("LongMethod")
    fun contribute(m: MutableMap<String, PropBinding>) {
        // shadow：复用 VisualPropContributor 已注册的底层 binding
        m.aliasShadow("effect.shadow.offsetX", "shadowOffsetX")
        m.aliasShadow("effect.shadow.offsetY", "shadowOffsetY")
        m.aliasShadow("effect.shadow.blur", "shadowBlur")
        m.aliasShadow("effect.shadow.color", "shadowColor")
        m.aliasShadow("effect.shadow.spread", "shadowSpread")

        // neon：直接登记到 StyleVisual.neon* 字段
        m.colorEffect("effect.neon.color", { vis, v -> vis.neonColor(v) }, { it.neonColor() })
        m.floatEffect("effect.neon.intensity", { vis, v -> vis.neonIntensity(v) }, { it.neonIntensity() })
        m.floatEffect("effect.neon.size", { vis, v -> vis.neonSize(v) }, { it.neonSize() })
        // 向后兼容旧版 DSL 中的 spread 别名（等同于 size）
        m.floatEffect("effect.neon.spread", { vis, v -> vis.neonSize(v) }, { it.neonSize() })
        m.boolEffect("effect.neon.animated", { vis, v -> vis.neonAnimated(v) }, { it.neonAnimated() })

        // ripple：直接登记到 StyleVisual.ripple* 字段
        m.colorEffect("effect.ripple.color", { vis, v -> vis.rippleColor(v) }, { it.rippleColor() })
        m.floatEffect("effect.ripple.duration", { vis, v -> vis.rippleDuration(v) }, { it.rippleDuration() })
        m.floatEffect("effect.ripple.radius", { vis, v -> vis.rippleRadius(v) }, { it.rippleRadius() })

        // procedural texture：第一批 8 种
        m.effect(
            "effect.texture.kind",
            { vis, v -> vis.proceduralTextureKind(v.toString()) },
            { it.proceduralTextureKind() },
        )
        m.colorEffect("effect.texture.colorA", { vis, v -> vis.proceduralColorA(v) }, { it.proceduralColorA() })
        m.colorEffect("effect.texture.colorB", { vis, v -> vis.proceduralColorB(v) }, { it.proceduralColorB() })
        m.floatEffect("effect.texture.scale", { vis, v -> vis.proceduralScale(v) }, { it.proceduralScale() })
        m.floatEffect("effect.texture.density", { vis, v -> vis.proceduralDensity(v) }, { it.proceduralDensity() })
        m.floatEffect("effect.texture.intensity", { vis, v ->
            vis.proceduralIntensity(v)
        }, { it.proceduralIntensity() })

        // flow：linear / radial / diagonal 流光
        m.colorEffect("effect.flow.color", { vis, v -> vis.flowColor(v) }, { it.flowColor() })
        m.floatEffect("effect.flow.speed", { vis, v -> vis.flowSpeed(v) }, { it.flowSpeed() })
        m.floatEffect("effect.flow.width", { vis, v -> vis.flowWidth(v) }, { it.flowWidth() })
        m.floatEffect("effect.flow.angleDeg", { vis, v -> vis.flowAngleDeg(v) }, { it.flowAngleDeg() })
        m.effect("effect.flow.mode", { vis, v -> vis.flowMode(parseFlowMode(v)) }, { it.flowMode() })
        // 仅 ProgressBar 生效：流光是否限制在前景（进度部分）流动。
        m.boolEffect("effect.flow.onProgress", { vis, v -> vis.flowOnProgress(v) }, { it.flowOnProgress() })

        // energy：脉冲 / 波浪 / 电弧 / 等离子
        m.colorEffect("effect.energy.color", { vis, v -> vis.energyColor(v) }, { it.energyColor() })
        m.floatEffect("effect.energy.intensity", { vis, v -> vis.energyIntensity(v) }, { it.energyIntensity() })
        m.floatEffect("effect.energy.frequency", { vis, v -> vis.energyFrequency(v) }, { it.energyFrequency() })
        m.floatEffect("effect.energy.speed", { vis, v -> vis.energySpeed(v) }, { it.energySpeed() })
        m.effect("effect.energy.pattern", { vis, v ->
            vis.energyPattern(parseEnergyPattern(v))
        }, { it.energyPattern() })

        // filter：E1 色彩矩阵（brightness/contrast/grayscale/sepia/hueRotate/saturate/invert），由 RenderStateStack 合成颜色矩阵包住子树
        m.floatEffect("effect.filter.brightness", { vis, v -> vis.filterBrightness(v) }, { it.filterBrightness() })
        m.floatEffect("effect.filter.contrast", { vis, v -> vis.filterContrast(v) }, { it.filterContrast() })
        m.floatEffect("effect.filter.grayscale", { vis, v -> vis.filterGrayscale(v) }, { it.filterGrayscale() })
        m.floatEffect("effect.filter.sepia", { vis, v -> vis.filterSepia(v) }, { it.filterSepia() })
        m.floatEffect("effect.filter.hueRotate", { vis, v -> vis.filterHueRotateDeg(v) }, { it.filterHueRotateDeg() })
        m.floatEffect("effect.filter.saturate", { vis, v -> vis.filterSaturate(v) }, { it.filterSaturate() })
        m.floatEffect("effect.filter.invert", { vis, v -> vis.filterInvert(v) }, { it.filterInvert() })

        // filter.blur：E2 高斯模糊（CSS filter: blur），走离屏 FBO 单 pass 二维高斯路径，此处仅承接 prop 路由
        m.floatEffect("effect.filter.blur", { vis, v -> vis.filterBlur(v) }, { it.filterBlur() })

        // #8 backdrop.blur：背景模糊（毛玻璃，CSS backdrop-filter: blur），与 filter.blur 走不同接缝，此处仅承接 prop 路由
        m.floatEffect("effect.backdrop.blur", { vis, v -> vis.backdropBlur(v) }, { it.backdropBlur() })

        // #8 blend.mode：混合模式（CSS mix-blend-mode），接受字符串别名解析为 GuiBlendMode；reader 返回 ordinal 与 flow.mode 数字读法一致
        m.effect("effect.blend.mode", { vis, v -> vis.blendMode(parseBlendMode(v)) }, { it.blendMode().ordinal })
    }

    /** 注册一个直登记到 [StyleVisual] 的 effects binding：统一处理「null 跳过 + 取 visual + wrap 异常翻译」。 */
    private fun MutableMap<String, PropBinding>.effect(
        key: String,
        writer: (StyleVisual, Any) -> Unit,
        reader: (StyleVisual) -> Any?,
    ) {
        this[key] = wrap(
            key,
            { el, v -> if (v != null) writer(el.style().visual(), v) },
            { el -> reader(el.style().visual()) },
        )
    }

    /** 颜色型 effect：值经 [ValueCoercion.toIntColor] 解析。 */
    private fun MutableMap<String, PropBinding>.colorEffect(
        key: String,
        set: (StyleVisual, Int) -> Unit,
        get: (StyleVisual) -> Int,
    ) = effect(key, { vis, v -> set(vis, ValueCoercion.toIntColor(v)) }, { get(it) })

    /** 浮点型 effect：值经 [ValueCoercion.toFloat] 解析；reader 返回 Double 与既有 transition 读法一致。 */
    private fun MutableMap<String, PropBinding>.floatEffect(
        key: String,
        set: (StyleVisual, Float) -> Unit,
        get: (StyleVisual) -> Float,
    ) = effect(key, { vis, v -> set(vis, ValueCoercion.toFloat(v)) }, { get(it).toDouble() })

    /** 布尔型 effect：值经 [ValueCoercion.toBool] 解析。 */
    private fun MutableMap<String, PropBinding>.boolEffect(
        key: String,
        set: (StyleVisual, Boolean) -> Unit,
        get: (StyleVisual) -> Boolean,
    ) = effect(key, { vis, v -> set(vis, ValueCoercion.toBool(v)) }, { get(it) })

    /** shadow 别名：复用底层 shadow* 的 applier / reader，额外包一层 effects 路径专用的异常翻译。 */
    private fun MutableMap<String, PropBinding>.aliasShadow(alias: String, target: String) {
        val binding = this[target]
        if (binding == null) {
            LOGGER.warn("effect 别名 $alias 找不到底层绑定 $target，已跳过")
            return
        }
        this[alias] = wrap(alias, binding.applier, binding.reader)
    }

    /**
     * 解析流光 mode：支持数字（0/1/2）与字符串别名（linear/radial/diagonal）。
     * 非法值统一抛 [IllegalStateException]，由 [wrap] 转换为 [DslRuntimeError]。
     */
    private fun parseFlowMode(v: Any?): Int = when (v) {
        is Number -> v.toInt().also {
            if (it < 0 || it > MAX_FLOW_MODE) error("非法的 flow.mode: $v（仅支持 0/1/2）")
        }
        is String -> when (v.trim().lowercase()) {
            "linear" -> 0
            "radial" -> 1
            "diagonal" -> MAX_FLOW_MODE
            else -> error("非法的 flow.mode: $v（仅支持 linear/radial/diagonal）")
        }
        else -> error("非法的 flow.mode: $v")
    }

    /**
     * 解析混合模式 blend.mode：支持 [GuiBlendMode] 实例、数字（序数）与字符串别名（normal/multiply/screen/additive）。
     * 非法值统一抛 [IllegalStateException]，由 [wrap] 转换为 [DslRuntimeError]。
     */
    private fun parseBlendMode(v: Any?): GuiBlendMode = when (v) {
        is GuiBlendMode -> v
        is Number -> {
            val values = GuiBlendMode.values()
            val n = v.toInt()
            if (n < 0 || n >= values.size) {
                error("非法的 blend.mode: $v（序数越界，仅支持 0..${values.size - 1}）")
            }
            values[n]
        }
        is String -> when (v.trim().lowercase()) {
            "normal" -> GuiBlendMode.NORMAL
            "multiply" -> GuiBlendMode.MULTIPLY
            "screen" -> GuiBlendMode.SCREEN
            "additive" -> GuiBlendMode.ADDITIVE
            else -> error("非法的 blend.mode: $v（仅支持 normal/multiply/screen/additive）")
        }
        else -> error("非法的 blend.mode: $v")
    }

    /** 解析能量 pattern：支持数字（0/1/2/3）与字符串别名（pulse/wave/arc/plasma）。 */
    private fun parseEnergyPattern(v: Any?): Int = when (v) {
        is Number -> v.toInt().also {
            if (it < 0 || it > MAX_ENERGY_PATTERN) error("非法的 energy.pattern: $v（仅支持 0/1/2/3）")
        }
        is String -> when (v.trim().lowercase()) {
            "pulse" -> 0
            "wave" -> 1
            "arc" -> 2
            "plasma" -> MAX_ENERGY_PATTERN
            else -> error("非法的 energy.pattern: $v（仅支持 pulse/wave/arc/plasma）")
        }
        else -> error("非法的 energy.pattern: $v")
    }

    /**
     * 包装 effects 路径上的 [PropBinding]：捕获 [ValueCoercion] 抛出的 [IllegalStateException] 并改抛
     * [DslRuntimeError]，打印元素 id 便于诊断；已经是 [DslRuntimeError] 的异常直接透传，避免双重包装。
     */
    private fun wrap(key: String, applier: PropApplier, reader: PropReader?): PropBinding = PropBinding.of(
        { el, v ->
            try {
                applier.apply(el, v)
            } catch (e: DslRuntimeError) {
                // 已经是 DSL 运行时错误，直接透传
                throw e
            } catch (e: IllegalStateException) {
                val elementId = el.id()
                LOGGER.warn("effects 属性 '$key' 类型不匹配（元素 id=$elementId）：${e.message}")
                throw DslRuntimeError("effects 属性 '$key' 类型不匹配（元素 id=$elementId）：${e.message}", e)
            }
        },
        reader,
    )
}
